import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getGA4ClientId, getGA4SessionId } from '@/lib/ga4Tracking';
import { cioIdentify, cioReset } from '@/lib/customerio';

/**
 * Tie the Customer.io profile to the signed-in Supabase user. Called on sign-in
 * and on session restoration; cioIdentify collapses repeats for the same user.
 * Reserved Customer.io attributes only: `email`, and `created_at` as Unix
 * seconds.
 */
const identifyToCustomerio = (user: User) => {
  const createdAt = user.created_at ? Math.floor(new Date(user.created_at).getTime() / 1000) : undefined;
  cioIdentify(user.id, {
    ...(user.email ? { email: user.email } : {}),
    ...(createdAt && Number.isFinite(createdAt) ? { created_at: createdAt } : {}),
  });
};

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier?: string;
  subscription_end?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  subscription: SubscriptionData | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithMagicLink: (email: string, redirectPath?: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  checkSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSubscription = async () => {
    if (!session) return;
    
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (error) {
        console.error('Subscription check error:', error);
        return;
      }
      
      setSubscription(data);
    } catch (error) {
      console.error('Subscription check failed:', error);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Sync GA4 user_id with auth state (first-party, hashed-by-Google)
        try {
          const w = window as any;
          if (typeof w.gtag === 'function') {
            if (session?.user?.id) {
              w.gtag('config', 'G-M09E85VL9J', { user_id: session.user.id });
            } else if (event === 'SIGNED_OUT') {
              w.gtag('config', 'G-M09E85VL9J', { user_id: null });
            }
          }
        } catch { /* no-op */ }

        // Customer.io Data In: identify after login (SIGNED_IN) and after the
        // SDK restores a stored session (INITIAL_SESSION / TOKEN_REFRESHED);
        // reset the profile on sign-out.
        try {
          if (session?.user?.id) {
            identifyToCustomerio(session.user);
          } else if (event === 'SIGNED_OUT') {
            cioReset();
          }
        } catch { /* no-op */ }

        // Handle cross-domain returnTo after sign-in
        if (event === 'SIGNED_IN' && session?.user) {
          const params = new URLSearchParams(window.location.search);
          const returnTo = params.get('returnTo');
          if (returnTo) {
            // If returnTo is a relative path, redirect back to marketing site
            const marketingOrigin = 'https://csquaregrc.com';
            const decodedPath = decodeURIComponent(returnTo);
            window.location.href = `${marketingOrigin}${decodedPath}`;
            return;
          }
        }
        
        // Defer subscription check to avoid callback deadlock
        if (session?.user) {
          setTimeout(() => {
            checkSubscription();
          }, 0);
        } else {
          setSubscription(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Seed GA4 user_id for already-signed-in visitors on initial load
      try {
        const w = window as any;
        if (session?.user?.id && typeof w.gtag === 'function') {
          w.gtag('config', 'G-M09E85VL9J', { user_id: session.user.id });
        }
      } catch { /* no-op */ }

      // Same for Customer.io: a restored session identifies once per load.
      try {
        if (session?.user?.id) identifyToCustomerio(session.user);
      } catch { /* no-op */ }

      if (session?.user) {
        setTimeout(() => {
          checkSubscription();
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const ga_client_id = getGA4ClientId();
    const ga_session_id = getGA4SessionId();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        // Persisted to auth.users.raw_user_meta_data so server-only contexts
        // (Stripe webhooks, etc.) can recover the original visitor's GA IDs.
        data: {
          ...(ga_client_id ? { ga_client_id } : {}),
          ...(ga_session_id ? { ga_session_id } : {}),
          ga_first_seen_at: new Date().toISOString(),
        },
      },
    });

    // If a session is returned (email confirmation disabled), persist GA IDs
    // onto the profile row immediately so they're queryable without parsing
    // user_metadata. Fire-and-forget — failures must not block sign-up.
    if (!error && data?.user?.id && (ga_client_id || ga_session_id)) {
      supabase
        .from('profiles')
        .update({
          ga_client_id: ga_client_id ?? null,
          ga_session_id: ga_session_id ?? null,
          ga_first_seen_at: new Date().toISOString(),
        })
        .eq('user_id', data.user.id)
        .then(({ error: profileError }) => {
          if (profileError) {
            console.warn('[AuthContext] Failed to persist GA IDs on profile', profileError);
          }
        });
    }

    return { error };
  };

  const signInWithGoogle = async () => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signInWithMagicLink = async (email: string, redirectPath: string = '/') => {
    const redirectUrl = `${window.location.origin}${redirectPath}`;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth`,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSubscription(null);
  };

  const value = {
    user,
    session,
    subscription,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithMagicLink,
    resetPassword,
    signOut,
    checkSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};